import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { AxiosError } from 'axios';
import { useToast } from '@common/toast';
import { useRenewAccessTokenRefreshToken } from '@repository/auth/useUserInfo';
import { useCouponCheck, useRegisterCoupons } from '@repository/coupons/useCoupons';
import VerificationCode from './VerificationCode';
import EventCodeList from './EventCodeList';
import VerificationCodeInfoVO from '@domain/vo/alliance/VerificationCodeInfoVO';
import RegisteredCouponListVO from '@domain/vo/coupons/RegisteredCouponVO';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import classnames from 'classnames/bind';
import styles from '../premium.module.scss';

const cx = classnames.bind(styles);

interface EventCodeRegisterFormProps {
  data: RegisteredCouponListVO;
  refetchData: () => void;
}

const EventCodeRegisterForm = ({ data, refetchData }: EventCodeRegisterFormProps) => {
  const Toast = useToast();

  const handleCheckCouponError = (e: AxiosError) => {
    switch (e.response?.data.errorCode) {
      case 'B901':
        setError(codeFieldName, { message: '존재하지 않는 코드입니다.' });
        return;
      case 'B902':
        setError(codeFieldName, { message: '이미 사용된 코드입니다.' });
        return;
      case 'B903':
        setError(codeFieldName, { message: '만료된 코드입니다.' });
        return;
    }
  };
  const { mutateAsync: checkCoupon } = useCouponCheck(handleCheckCouponError);

  const handleRegisterCouponError = (e: AxiosError) => {
    switch (e.response?.data.errorCode) {
      case 'B002':
        Toast({ type: 'error', iconType: 'info', content: '존재하지 않는 유저입니다.' });
        return;
      case 'B901':
        Toast({ type: 'error', iconType: 'info', content: '존재하지 않는 코드입니다.' });
        return;
      case 'B902':
        Toast({ type: 'error', iconType: 'info', content: '이미 사용된 코드입니다.' });
        return;
      case 'B903':
        Toast({ type: 'error', iconType: 'info', content: '만료된 코드입니다.' });
        return;
      default:
        Toast({ type: 'error', iconType: 'info', content: e.response?.data.errorMessage });
        return;
    }
  };
  const { mutateAsync: registerCoupon } = useRegisterCoupons(handleRegisterCouponError);
  const { mutateAsync: fetchRefreshToken } = useRenewAccessTokenRefreshToken();
  const [isCertStatusSuccess, setIsCertStatusSuccess] = useState(false);

  const methods = useForm<VerificationCodeInfoVO>({
    mode: 'onChange',
  });

  const { handleSubmit, watch, setError, reset } = methods;

  const codeFieldName = 'code';
  const codeValue = watch(codeFieldName);

  const checkCertStatus = async () => {
    const result = await checkCoupon(codeValue);
    if (result) {
      setIsCertStatusSuccess(true);
    }
  };

  const reload = async () => {
    //제휴 서비스 인증 권한 추가 하기 위한 토큰 재발급/사용자 정보 reload
    await fetchRefreshToken();
    refetchData();
    reset({});
    setIsCertStatusSuccess(false);
  };

  const onSubmit = async (data: VerificationCodeInfoVO) => {
    await registerCoupon(data.code);
    Toast({
      iconType: 'check',
      type: 'success',
      content: '인증이 완료되었습니다.',
    });
    await reload();
  };

  return (
    <div className={cx('contentsWrap')}>
      <div className={cx('requireTextArea')}>
        <span className={cx('requireText')}>필수 입력</span>
      </div>
      <FormProvider {...methods}>
        <form
          autoComplete={'off'}
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnter}
        >
          <VerificationCode
            name={codeFieldName}
            isCertStatusSuccess={isCertStatusSuccess}
            checkCertStatus={checkCertStatus}
          />
          <EventCodeList data={data} />
          <div className={cx('btnWrap')}>
            <button className={cx('btnSave')} type={'submit'} disabled={!isCertStatusSuccess}>
              제휴 서비스 신청
            </button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default EventCodeRegisterForm;
